import { Fragment } from "react";
import { useTranslate } from "@refinedev/core";
import { Flex, GlobalToken, Typography } from "antd";
import styled from "styled-components";
import { Divider, Eyebrow, radius } from "@libertify/ui";
import {
  ChatCircleDotsIcon,
  GraduationCapIcon,
  LightbulbIcon,
  MapTrifoldIcon,
  WaveformIcon,
} from "@phosphor-icons/react";
import { useAntTheme } from "hooks/useAntTheme";
import type { CreatorArchetype } from "pages/creator-profile/types";
import { getDnaArtwork, getDnaCopyKey } from "../creatorDnaArchetypes";
import { DnaDimensionRow } from "./DnaDimensionRow";

// Row order and icon per dimension; the letter comes from the archetype.
const DIMENSIONS = [
  { dimension: "illustration", icon: LightbulbIcon },
  { dimension: "structure", icon: MapTrifoldIcon },
  { dimension: "voice", icon: WaveformIcon },
  { dimension: "depth", icon: GraduationCapIcon },
  { dimension: "autonomy", icon: ChatCircleDotsIcon },
] as const;

export type CreatorDnaDetailsProps = Readonly<{
  archetype: CreatorArchetype;
}>;

export const CreatorDnaDetails = ({ archetype }: CreatorDnaDetailsProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const artwork = getDnaArtwork(archetype.name);
  // Copy exists only for archetypes we ship artwork for; otherwise show the API name.
  const isKnown = !!artwork;

  const tagline: unknown = isKnown
    ? t(getDnaCopyKey(archetype.name, "tagline"), {
        returnObjects: true,
        defaultValue: [],
      })
    : [];

  return (
    <StyledLayout>
      <StyledArtworkColumn $token={theme} align="center" justify="center">
        {artwork ? <StyledArtwork src={artwork} alt={archetype.name} /> : null}
      </StyledArtworkColumn>

      <StyledInfoColumn vertical>
        <StyledHeader $token={theme} vertical gap={4}>
          <Eyebrow size="s">
            {`${archetype.code} · ${archetype.setting_code}`}
          </Eyebrow>
          <Typography.Title level={3} style={{ margin: 0, fontWeight: 700 }}>
            {isKnown
              ? t(getDnaCopyKey(archetype.name, "name"))
              : archetype.name}
          </Typography.Title>
          {Array.isArray(tagline) && tagline.length > 0 ? (
            <Typography.Text strong style={{ color: theme.colorPrimary }}>
              {tagline.join(" · ")}
            </Typography.Text>
          ) : null}
          {isKnown ? (
            <Typography.Text type="secondary">
              {t(getDnaCopyKey(archetype.name, "description"))}
            </Typography.Text>
          ) : null}
        </StyledHeader>

        <StyledTraits $token={theme} vertical gap={theme.marginLG}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t("pages.user.edit.creatorDna.traitsTitle")}
          </Typography.Title>
          {DIMENSIONS.map(({ dimension, icon }, index) => (
            <Fragment key={dimension}>
              {index > 0 ? <Divider spacing="none" /> : null}
              <DnaDimensionRow
                icon={icon}
                label={t(`creatorDna.dimensionLabels.${dimension}`)}
                title={t(
                  `creatorDna.dimensions.${dimension}.${archetype[dimension]}.title`
                )}
                body={t(
                  `creatorDna.dimensions.${dimension}.${archetype[dimension]}.body`
                )}
              />
            </Fragment>
          ))}
        </StyledTraits>
      </StyledInfoColumn>
    </StyledLayout>
  );
};

// ─── Styled components ───

// Fixed height so the traits column scrolls inside the modal instead of the page.
const StyledLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: min(720px, calc(100vh - 220px));
`;

const StyledArtworkColumn = styled(Flex)<{ $token: GlobalToken }>`
  padding: ${({ $token }) => $token.paddingLG}px;
`;

const StyledArtwork = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${radius.box}px;
`;

const StyledInfoColumn = styled(Flex)`
  overflow-y: auto;
`;

const StyledHeader = styled(Flex)<{ $token: GlobalToken }>`
  padding: ${({ $token }) => $token.paddingLG}px;
  background: ${({ $token }) => $token.colorFillQuaternary};
`;

const StyledTraits = styled(Flex)<{ $token: GlobalToken }>`
  padding: ${({ $token }) => $token.paddingLG}px;
`;
